import React, { useRef } from "react";
import { connect } from "react-redux";
import { useDrag, useDrop } from "react-dnd";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import classNames from "classnames";
import "./index.scss";

import { createAction } from "../../../utils/sagaHelper";
import { ORDER_TYPE, DELIVERY_STRATEGY, WAITER_TYPES, INTEGRATION_TYPE, getCurrencyIcons } from '../../../utils/enums';
import { defaultPromiseResolve } from "../../../utils/objects-util";
import {
  getPlaceOrAddress,
  getPickupPlaceOrAddress,
  getOrderTime,
  getConvertedTargetTime,
  isEmptyEstimationData,
  getIsShowReadinessTime
} from "../../../services/order";
import { getAppartmentNumber } from './../../../services/order';
import { getDepotName, getDepotPhone, getDepotAvatar, getLabelWithMetric } from '../../../services/restaurant';
import { CHANGE_ORDER_INDEX } from "../../../store/reducers/order/order-actions";

import OrderStateControl from "../OrderStateControl";
import Waiter from "../Waiter/Waiter";
import IconButton from "../IconButton";
import ReadinessTimeInfo from "../ReadinessTimeInfo";
import { DND_TYPES } from "../../../utils/constants";
import TimeAsap from "../TimeAsap";
import { fixedNumber } from '../../../utils/math-util.js';

const GroupbarOrderItem = ({
  t,
  order,
  orders,
  groups,
  selected,
  isHideOrderFooter,
  changeOrderIndex,
  onOrderClick,
  onOrderTitleClick,
  onOpenAssociatePopup,
  onOpenEditForm,
  onUnassociateOrder,
  onDrop,
  onSetBaseWaiter,
  estimatedPrice
}) => {
  const dragPlaceholderRef = useRef();
  const [{ isDragging, opacity }, drag] = useDrag(() => ({
    type: DND_TYPES.group_bar_order,
    item: order,
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult() || {};
      if (item && dropResult.order && onDrop) {
        onDrop(item.id, dropResult.order.id);
      }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
      opacity: monitor.isDragging() ? 0.9 : 1
    }),
  }), [order, onDrop]);

  const [, drop] = useDrop(() => ({
    accept: DND_TYPES.group_bar_order,
    drop: () => ({ order })
  }), [order])

  const pickupIds = () => {
    if (!order.items || !order.items.length) return "";
    const items = orders.filter(f => order.items.includes(f.id) && f.orderId);
    return items ? items.map(m => m.orderId).join(", ") : "";
  };

  const checkEstimateData = () => {
    const group = groups.find(g => g.id === order.groupId);
    return !isEmptyEstimationData(group) || getOrderTime(order.id);
  };

  const handleOnChangeOrderIndex = newIndex => {
    onSetBaseWaiter(true).then(()=> {
      changeOrderIndex({ order, newIndex }).then(()=> {
        onSetBaseWaiter(false);
      }, error=> {
        toast.error(error);
        onSetBaseWaiter(false);
      });
    });    
  }

  const depotAvatar = order.type === ORDER_TYPE.pickUp ? getDepotAvatar(order) : null;

  return (
    <div
      id={order.id}
      key={order.id}
      ref={drag}
      className={classNames('groupbarOrderItem', { selected: selected })}
      style={{ cursor: isDragging ? 'move' : 'pointer', opacity }}
    >
      <div className={classNames('data')} ref={drop}>
        <div className={classNames('leftSide')}>
          <div className={classNames('gripper')} />
          <div className={classNames('arrow-top')} onClick={() => handleOnChangeOrderIndex(order.index - 1)} />
          <div className={classNames('arrow-bottom')} onClick={() => handleOnChangeOrderIndex(order.index + 1)} />
        </div>
        <div
          className={classNames('centerSide')}
          onClick={() => {
            order.type !== ORDER_TYPE.pickUp && onOrderClick(order.id);
          }}
        >
          <div className={classNames('title', { vip: order.isHighPriority })} onClick={() => onOrderTitleClick(order)}>
            {order.isHighPriority && <IconButton isIcon={true} iconName='crown' onClick={defaultPromiseResolve} />}
            {order.pODRequired && <IconButton isIcon iconName={'podRequired'} />}
            {getDepotName(order)}
            {order.type !== ORDER_TYPE.pickUp && <span className={classNames('divider')}>&nbsp;/&nbsp;</span>}
            {order.type !== ORDER_TYPE.pickUp && <span>{order.orderId}</span>}
          </div>
          <div className={classNames('info')}>
            <div className={classNames('infoItem')}>
              <span className={classNames('addressIcon')} />
              <span>
                {order.type !== ORDER_TYPE.pickUp ? getPlaceOrAddress(order) : getPickupPlaceOrAddress(order)}
                {getAppartmentNumber(order) && (
                  <span>
                    &nbsp;{t('dashboard.orders.APPARTMENT')}:&nbsp;{getAppartmentNumber(order)}
                  </span>
                )}
              </span>
            </div>
            {order?.address?.notes && (
              <div className={classNames('infoItem')}>
                <span className={classNames('notesIcon')} />
                <span className={classNames('notes')}>{order.address.notes}</span>
              </div>
            )}
            <div className={classNames('infoItem')}>
              <span className={classNames('notesIcon')} />
              <span className={classNames('notes')}>
                {order.type !== ORDER_TYPE.pickUp ? order.notes || '--' : pickupIds()}
              </span>
            </div>
            <div className={classNames('infoItem')}>
              <span className={classNames('phoneIcon')} />
              <span>
                {order.type !== ORDER_TYPE.pickUp ? order.consumerPhone || '--' : getDepotPhone(order) || '--'}
              </span>
            </div>
            {estimatedPrice || estimatedPrice === 0 ? (
              <div className={classNames('infoItem')}>
                <span className={classNames('coinsIcon')}></span>
                <span>
                  {order?.ridingDistance &&
                    <span>
                      {parseFloat((order?.ridingDistance / 1000).toFixed(2))}{' '}
                      {t(getLabelWithMetric('dashboard.orders.DISTANCE_LABEL'))},
                    </span>
                  }{' '}
                  {t('dashboard.orders.FEE')}: {estimatedPrice}{getCurrencyIcons(order.currency)}
                  {!!order.courierFeeBidIncrease && ` +${fixedNumber(order.courierFeeBidIncrease * 100, 2)}%`}
                </span>
              </div>
            ) : (order?.ridingDistance &&
              <div className={classNames('infoItem')}>
                <span>
                  {parseFloat((order?.ridingDistance / 1000).toFixed(2))}{' '}
                  {t(getLabelWithMetric('dashboard.orders.DISTANCE_LABEL'))}
                </span>
              </div>
            )}
          </div>
        </div>
        {order.type === ORDER_TYPE.pickUp && (
          <div className={classNames('rightSide', 'depotRightSide')}>
            {depotAvatar && <img alt='depot-avatar' src={depotAvatar} />}
            {checkEstimateData() && (
              <div className={classNames('block-time', 'depot-block-time')}>
                <div className={classNames('segment-img')} />
                <div>{getOrderTime(order)}</div>
              </div>
            )}
          </div>
        )}
        {order.type !== ORDER_TYPE.pickUp && (
          <div className={classNames('rightSide')}>
            <div className={classNames('block-time', { readiness: getIsShowReadinessTime(order) })}>
              <TimeAsap value={order} isWithIcon={true} />
              {order.deliveryStrategy === DELIVERY_STRATEGY.concreteTime && (
                <div className={classNames('targetTimeBlock')}>
                  <IconButton
                    tooltipText={t('restaurateur.orders.PREOPDER_TARGET_TIME')}
                    isIcon={true}
                    iconName={'targetTimes'}
                  />
                  <span>{getConvertedTargetTime(order, true)}</span>
                </div>
              )}
              <ReadinessTimeInfo withIcon value={order} />
            </div>
            <div className={classNames('associateContainer')}>
              <OrderStateControl order={order} hideAssociate={true} />
              <div className={classNames('order-arrow')}>
                <div className={classNames('arrow-icon')} />
              </div>
            </div>
            <div className={classNames('block-time')}>
              {order.integrationType === INTEGRATION_TYPE.ordering && (
                <IconButton isIcon={true} iconName={'appStore'} onClick={defaultPromiseResolve} />
              )}
              {order.integrationType === INTEGRATION_TYPE.kiosk && (
                <IconButton isIcon={true} iconName={'kiosk'} onClick={defaultPromiseResolve} />
              )}
              &nbsp;
              <div className={classNames('segment-img')} />
              <div>{getOrderTime(order)}</div>
            </div>
          </div>
        )}
      </div>
      {!isHideOrderFooter && (
        <div className={classNames('footer')}>
          {selected && (
            <div className={classNames('footerControls')}>
              <div className={classNames('footerControl')}>
                <Waiter
                  tooltipText={t('dashboard.orders.ASSOCIATE_BUTTON')}
                  useWrapClass='controlAction'
                  useTextClass='actionDescription'
                  useClass={classNames('actionIcon', 'courierActionIcon')}
                  spanText={t('dashboard.orders.ASSOCIATE_LABEL')}
                  type={WAITER_TYPES.buttonSpan}
                  handleOnClick={() => onOpenAssociatePopup(order.id)}
                />
              </div>
              <div className={classNames('footerControl')}>
                <Waiter
                  tooltipText={t('dashboard.orders.DISBAND_GROUP_ORDER_TOOLTIP')}
                  useWrapClass='controlAction'
                  useTextClass='actionDescription'
                  useClass={classNames('actionIcon', 'groupActionIcon')}
                  spanText={t('dashboard.orders.DISBAND_GROUP_ORDER')}
                  type={WAITER_TYPES.buttonSpan}
                  handleOnClick={() => onUnassociateOrder(order.id)}
                />
              </div>
              <div className={classNames('footerControl')}>
                <Waiter
                  tooltipText={t('dashboard.orders.EDIT_ORDER_BUTTON')}
                  useWrapClass='controlAction'
                  useTextClass='actionDescription'
                  useClass={classNames('actionIcon', 'editActionIcon')}
                  spanText={t('dashboard.orders.EDIT_ORDER_LABEL')}
                  type={WAITER_TYPES.buttonSpan}
                  handleOnClick={() => onOpenEditForm(order.id)}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <div
        ref={dragPlaceholderRef}
        className={classNames('dragPlaceholder')}
        style={{ display: isDragging ? 'block' : 'none', zIndex: 1 }}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  orders: state.order.data.orders || [],
  groups: state.order.data.groups || []
});

const mapDispatchToProps = dispatch => {
  return {
    changeOrderIndex: data => dispatch(createAction(CHANGE_ORDER_INDEX, data)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(GroupbarOrderItem));
